<template>
  <main class="ch-page">
    <!-- SEASONAL NOTICE -->
    <div class="season-banner" role="status">
      Closed for the season. Some weekend availability &ndash; inquire.
    </div>

    <!-- HERO -->
    <section class="hero">
      <div class="hero__media">
        <img src="@/assets/house.jpg" alt="Chestnut Holler B&B — bright green century-old home in Southwest Nova Scotia" />
      </div>
      <div class="hero__content">
        <p class="eyebrow">Southwest Nova Scotia · Barrington</p>
        <h1>Welcome to Chestnut Holler Guesthouse</h1>
        <p class="lede">
          Enjoy your stay on the private second floor of a century-old home in a small coastal village in Southwest Nova Scotia.
        </p>
        <div class="hero__actions">
          <a class="btn btn--primary" href="mailto:reservations@chestnutholler.com">
            <span aria-hidden="true">✉</span> Email to Book
          </a>
          <a class="btn btn--ghost" href="tel:902-320-7183">
            <span aria-hidden="true">📱</span> 902-320-7183
          </a>
        </div>
        <router-link class="hero__concierge" to="/concierge">
          <span class="hero__concierge-icon" aria-hidden="true">🛎️</span>
          <span class="hero__concierge-text">
            <strong>Ask our Local Area Concierge</strong>
            <span>Things to do, dining &amp; beaches around Barrington</span>
          </span>
          <span class="hero__concierge-arrow" aria-hidden="true">→</span>
        </router-link>
        <ul class="hero__badges">
          <li>★ Booking.com 9.8</li>
          <li>★ AirBnb 5/5</li>
          <li>Canada Select 3.5</li>
        </ul>
      </div>
    </section>

    <!-- PRICING -->
    <section class="section pricing">
      <div class="section__inner">
        <p class="eyebrow">Honest, budget-friendly rates</p>
        <div class="price-grid">
          <article class="price-card">
            <h3>One Room</h3>
            <p class="price"><span class="dollar">$</span>100<span class="per">/night</span></p>
            <p class="price-note">+ HST · sleeps up to 2</p>
          </article>
          <article class="price-card price-card--featured">
            <span class="ribbon">Best for Couples &amp; Friends</span>
            <h3>Two Rooms</h3>
            <p class="price"><span class="dollar">$</span>140<span class="per">/night</span></p>
            <p class="price-note">+ HST · whole private floor</p>
          </article>
        </div>
        <p class="min-stay">Free Level 2 EV charging included</p>
      </div>
    </section>

    <!-- AMENITIES -->
    <section class="section amenities">
      <div class="section__inner">
        <h2>Your Stay Includes</h2>
        <ul class="amenity-grid">
          <li><span class="amenity__icon">🛏️</span><span>Two cozy guest rooms with double beds</span></li>
          <li><span class="amenity__icon">🚿</span><span>A private 4-piece bathroom</span></li>
          <li><span class="amenity__icon">🍳</span><span>A convenient kitchenette</span></li>
          <li><span class="amenity__icon">📶</span><span>WiFi &amp; Smart TVs</span></li>
          <li><span class="amenity__icon">⚡</span><span>Free Level 2 EV charging (8.5 kW)</span></li>
          <li><span class="amenity__icon">🏳️‍🌈</span><span>2SLGBTQ+ friendly — everyone welcome</span></li>
        </ul>
      </div>
    </section>

    <!-- CONTACT -->
    <section class="section contact">
      <div class="section__inner">
        <h2>Contact Us the Old-Fashioned Way</h2>
        <p>We're proudly off the booking platforms! To reserve your stay, please contact us directly:</p>
        <div class="contact__links">
          <a class="contact__link" href="mailto:reservations@chestnutholler.com">
            <span aria-hidden="true">📧</span>
            <span>reservations@chestnutholler.com</span>
          </a>
          <a class="contact__link" href="tel:902-320-7183">
            <span aria-hidden="true">📱</span>
            <span>902-320-7183</span>
          </a>
        </div>
        <p class="location-tag">📍 Just 45 minutes from the Yarmouth Ferry</p>
        <p class="contact__concierge">
          Planning your trip? <router-link to="/concierge">Chat with our local-area concierge</router-link>
          for things to do, dining, and beaches nearby.
        </p>
      </div>
    </section>

    <!-- GOOD-TO-KNOW + CAT -->
    <section class="section good-to-know">
      <div class="section__inner two-col">
        <div>
          <h2>Good to Know</h2>
          <p>
            Rooms not accessible (stairs). No food service (coffee/tea and some breakfast items provided).
            Level 2 EV charging available (8.5kw). Cats on premises. Queer friendly.
          </p>
        </div>
        <figure class="cat">
          <img src="@/assets/grizzle.jpg" alt="Grizzle the resident cat" />
          <figcaption>Grizzle says hi</figcaption>
        </figure>
      </div>
    </section>

    <!-- ROOM GALLERY -->
    <section class="section gallery">
      <div class="section__inner">
        <h2>The Rooms</h2>
        <div class="gallery__grid">
          <figure>
            <img src="@/assets/room1.png" alt="Guest room one" />
          </figure>
          <figure>
            <img src="@/assets/room2.png" alt="Guest room two" />
          </figure>
        </div>
      </div>
    </section>

    <!-- FINAL CTA -->
    <section class="section cta-final">
      <div class="section__inner">
        <h2>Ready to Book?</h2>
        <p>Email us — we'll get back to you quickly.</p>
        <div class="hero__actions">
          <a class="btn btn--primary" href="mailto:reservations@chestnutholler.com">✉ reservations@chestnutholler.com</a>
          <a class="btn btn--ghost" href="tel:902-320-7183">📱 902-320-7183</a>
        </div>
      </div>
    </section>

    <!-- FOOTER -->
    <footer class="footer">
      <p class="footer__trust">
        Nova Scotia Licensed Tourist Accommodation #STR2526B1823 (2025) ·
        Canada Select 3.5 stars · Booking.com 9.8/10 · AirBnb 5/5
      </p>
      <p class="footer__contact">
        <a href="mailto:reservations@chestnutholler.com">reservations@chestnutholler.com</a>
        ·
        <a href="tel:902-320-7183">902-320-7183</a>
      </p>
      <p class="footer__credit">
        Site built by <a href="https://topsailsoftware.ca" target="_blank" rel="noopener">topsailsoftware.ca</a>
      </p>
    </footer>

    <!-- Sticky mobile CTA -->
    <a class="mobile-cta" href="mailto:reservations@chestnutholler.com">✉ Book Your Stay</a>
  </main>
</template>

<script>
export default {
  name: 'HomeView'
}
</script>

<style scoped>
:root {
  --ink: #1a1d23;
  --ink-soft: #4a5160;
  --cream: #fdf7ec;
  --paper: #ffffff;
  --sand: #f4ead8;
  --orange: #e8741e;
  --orange-dark: #c45a0c;
  --teal: #1f5f5b;
  --teal-dark: #163f3c;
  --shadow: 0 10px 30px rgba(22, 30, 40, 0.08);
  --shadow-lg: 0 20px 50px rgba(22, 30, 40, 0.12);
  --radius: 18px;
}

.ch-page {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  color: #1a1d23;
  background: #fdf7ec;
  text-align: left;
  line-height: 1.55;
  padding-bottom: 80px;
}

.ch-page h1,
.ch-page h2,
.ch-page h3 {
  font-family: 'Georgia', 'Times New Roman', serif;
  letter-spacing: -0.01em;
  line-height: 1.15;
  color: #163f3c;
  margin: 0 0 .5em;
}

.ch-page h1 { font-size: clamp(2rem, 5.5vw, 3.25rem); }
.ch-page h2 { font-size: clamp(1.5rem, 3.5vw, 2.25rem); }
.ch-page h3 { font-size: 1.25rem; }

.ch-page p { margin: 0 0 1em; color: #4a5160; }

.season-banner {
  background: #163f3c;
  color: #ffffff;
  text-align: center;
  font-weight: 600;
  padding: 12px 16px;
}

.eyebrow {
  text-transform: uppercase;
  letter-spacing: .14em;
  font-size: .8rem;
  font-weight: 700;
  color: #e8741e !important;
  margin: 0 0 .75em;
}

/* ---------- Hero ---------- */
.hero {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  padding: 1.5rem 1.25rem 2rem;
  max-width: 1100px;
  margin: 0 auto;
  align-items: center;
}

.hero__media img {
  width: 100%;
  height: auto;
  border-radius: 22px;
  box-shadow: 0 20px 50px rgba(22, 30, 40, 0.12);
  display: block;
}

.hero__content { padding: .5rem .25rem; }
.hero .lede { font-size: 1.125rem; color: #4a5160; max-width: 52ch; }

.hero__actions {
  display: flex;
  gap: .75rem;
  flex-wrap: wrap;
  margin: 1.25rem 0 1rem;
}

.hero__concierge {
  display: inline-flex;
  align-items: center;
  gap: .8rem;
  margin: 1rem 0 0;
  padding: .8rem 1.1rem;
  background: linear-gradient(160deg, #eef6f2 0%, #fdf7ec 100%);
  border: 1px solid #cfe3da;
  border-radius: 16px;
  text-decoration: none;
  box-shadow: 0 8px 20px rgba(22, 63, 60, 0.08);
  transition: transform .15s ease, box-shadow .15s ease, border-color .15s ease;
}
.hero__concierge:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 30px rgba(22, 63, 60, 0.14);
  border-color: #1f5f5b;
}
.hero__concierge-icon { font-size: 1.5rem; line-height: 1; }
.hero__concierge-text {
  display: flex;
  flex-direction: column;
  gap: .1rem;
}
.hero__concierge-text strong {
  color: #1f5f5b;
  font-size: .95rem;
}
.hero__concierge-text span {
  color: #4a5160;
  font-size: .8rem;
}
.hero__concierge-arrow {
  color: #e8741e;
  font-weight: 700;
  font-size: 1.1rem;
  transition: transform .15s ease;
}
.hero__concierge:hover .hero__concierge-arrow { transform: translateX(3px); }

.hero__badges {
  list-style: none;
  padding: 0;
  margin: 1rem 0 0;
  display: flex;
  flex-wrap: wrap;
  gap: .5rem;
}
.hero__badges li {
  font-size: .8rem;
  font-weight: 600;
  background: #ffffff;
  color: #163f3c;
  padding: .35rem .7rem;
  border-radius: 999px;
  border: 1px solid #f4ead8;
}

/* ---------- Buttons ---------- */
.btn {
  display: inline-flex;
  align-items: center;
  gap: .5rem;
  padding: .85rem 1.25rem;
  border-radius: 999px;
  font-weight: 700;
  font-size: 1rem;
  text-decoration: none;
  transition: transform .15s ease, box-shadow .15s ease, background .15s ease;
  white-space: nowrap;
}
.btn--primary {
  background: #e8741e;
  color: #ffffff !important;
  box-shadow: 0 10px 24px rgba(232, 116, 30, 0.35);
}
.btn--primary:hover { background: #c45a0c; transform: translateY(-1px); }
.btn--ghost {
  background: transparent;
  color: #163f3c !important;
  border: 2px solid #163f3c;
}
.btn--ghost:hover { background: #163f3c; color: #ffffff !important; }

/* ---------- Section frame ---------- */
.section { padding: 2.5rem 1.25rem; }
.section__inner {
  max-width: 1000px;
  margin: 0 auto;
}

/* ---------- Pricing ---------- */
.pricing { background: #ffffff; }
.price-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin: 1.25rem 0 1rem;
}
.price-card {
  position: relative;
  background: #fdf7ec;
  border: 1px solid #f4ead8;
  border-radius: 18px;
  padding: 1.75rem 1.5rem;
  text-align: center;
  box-shadow: 0 10px 30px rgba(22, 30, 40, 0.04);
  transition: transform .2s ease, box-shadow .2s ease;
}
.price-card:hover { transform: translateY(-3px); box-shadow: 0 20px 50px rgba(22, 30, 40, 0.12); }
.price-card--featured {
  background: linear-gradient(160deg, #1f5f5b 0%, #163f3c 100%);
  border-color: #163f3c;
  color: #fdf7ec;
}
.price-card--featured h3,
.price-card--featured .price-note { color: #fdf7ec; }
.price-card h3 { margin-bottom: .25rem; }
.price {
  font-family: 'Georgia', serif;
  font-size: 3.5rem;
  font-weight: 700;
  color: #e8741e;
  margin: .5rem 0 .25rem;
  line-height: 1;
}
.price .dollar { font-size: 1.75rem; vertical-align: top; margin-right: .15rem; }
.price .per { font-size: 1rem; color: #4a5160; font-weight: 500; margin-left: .25rem; }
.price-card--featured .price { color: #ffb074; }
.price-card--featured .price .per { color: #fdf7ec; opacity: .8; }
.price-note { font-size: .95rem; color: #4a5160; margin: 0; }
.ribbon {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: #e8741e;
  color: #ffffff;
  font-size: .75rem;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
  padding: .35rem .85rem;
  border-radius: 999px;
  white-space: nowrap;
}
.min-stay {
  text-align: center;
  font-weight: 600;
  color: #163f3c;
  background: #f4ead8;
  display: inline-block;
  padding: .65rem 1.15rem;
  border-radius: 999px;
  margin: 1rem auto 0;
}
.pricing .section__inner { text-align: center; }

/* ---------- Amenities ---------- */
.amenities { background: #fdf7ec; }
.amenity-grid {
  list-style: none;
  padding: 0;
  margin: 1rem 0 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: .75rem;
}
.amenity-grid li {
  display: flex;
  align-items: center;
  gap: .85rem;
  background: #ffffff;
  border-radius: 14px;
  padding: 1rem 1.15rem;
  box-shadow: 0 4px 14px rgba(22, 30, 40, 0.05);
  font-weight: 500;
  color: #1a1d23;
}
.amenity__icon {
  font-size: 1.6rem;
  width: 2.25rem;
  text-align: center;
  flex-shrink: 0;
}

/* ---------- Contact ---------- */
.contact { background: #163f3c; color: #fdf7ec; text-align: center; }
.contact h2 { color: #fdf7ec; }
.contact p { color: #d9cbb0; }
.contact__links {
  display: flex;
  flex-direction: column;
  gap: .75rem;
  align-items: center;
  margin: 1.5rem 0 1rem;
}
.contact__link {
  display: inline-flex;
  align-items: center;
  gap: .65rem;
  background: #ffffff;
  color: #163f3c !important;
  padding: .85rem 1.5rem;
  border-radius: 999px;
  font-weight: 700;
  text-decoration: none;
  box-shadow: 0 10px 24px rgba(0,0,0,0.18);
  transition: transform .15s ease;
  word-break: break-word;
}
.contact__link:hover { transform: translateY(-2px); }
.location-tag { color: #ffb074 !important; font-weight: 600; margin-top: 1rem; }
.contact__concierge { color: #d9cbb0; margin-top: 1rem; }
.contact__concierge a { color: #ffb074 !important; font-weight: 700; text-decoration: underline; }

/* ---------- Good to know + cat ---------- */
.good-to-know { background: #fdf7ec; }
.two-col {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  align-items: center;
}
.cat { margin: 0; text-align: center; }
.cat img {
  width: 100%;
  max-width: 320px;
  border-radius: 18px;
  box-shadow: 0 10px 30px rgba(22, 30, 40, 0.08);
}
.cat figcaption {
  margin-top: .5rem;
  font-style: italic;
  color: #4a5160;
  font-size: .9rem;
}

/* ---------- Gallery ---------- */
.gallery { background: #ffffff; }
.gallery__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-top: 1rem;
}
.gallery figure { margin: 0; }
.gallery img {
  width: 100%;
  height: auto;
  border-radius: 18px;
  box-shadow: 0 10px 30px rgba(22, 30, 40, 0.08);
  display: block;
}

/* ---------- Final CTA ---------- */
.cta-final {
  background: linear-gradient(160deg, #e8741e 0%, #c45a0c 100%);
  text-align: center;
}
.cta-final h2, .cta-final p { color: #ffffff; }
.cta-final .btn--primary {
  background: #ffffff;
  color: #c45a0c !important;
  box-shadow: 0 10px 24px rgba(0,0,0,0.2);
}
.cta-final .btn--primary:hover { background: #fdf7ec; }
.cta-final .btn--ghost {
  border-color: #ffffff;
  color: #ffffff !important;
}
.cta-final .btn--ghost:hover { background: #ffffff; color: #c45a0c !important; }
.cta-final .hero__actions { justify-content: center; }

/* ---------- Footer ---------- */
.footer {
  background: #1a1d23;
  color: #d9cbb0;
  text-align: center;
  padding: 2rem 1.25rem 6rem;
  font-size: .9rem;
}
.footer p { color: #d9cbb0; margin: .35rem 0; }
.footer a { color: #ffb074 !important; text-decoration: none; }
.footer a:hover { text-decoration: underline; }
.footer .footer__trust { max-width: 620px; margin: 0 auto .5rem; }
.footer__credit { margin-top: 1rem; opacity: .7; font-size: .8rem; }

/* ---------- Sticky mobile CTA ---------- */
.mobile-cta {
  display: none;
  position: fixed;
  left: 1rem;
  right: 1rem;
  bottom: 1rem;
  z-index: 50;
  background: #e8741e;
  color: #ffffff !important;
  text-align: center;
  padding: 1rem;
  border-radius: 999px;
  font-weight: 700;
  text-decoration: none;
  box-shadow: 0 12px 30px rgba(0,0,0,0.25);
}

@media (max-width: 720px) {
  .mobile-cta { display: block; }
  .footer { padding-bottom: 6rem; }
}

/* ---------- Responsive up ---------- */
@media (min-width: 720px) {
  .hero {
    grid-template-columns: 1fr 1fr;
    gap: 2.5rem;
    padding: 3rem 1.5rem;
  }
  .hero__content { order: -1; }
  .price-grid { grid-template-columns: 1fr 1fr; gap: 1.5rem; }
  .amenity-grid { grid-template-columns: 1fr 1fr; }
  .two-col { grid-template-columns: 2fr 1fr; gap: 2.5rem; }
  .gallery__grid { grid-template-columns: 1fr 1fr; gap: 1.5rem; }
  .section { padding: 4rem 1.5rem; }
}

@media (min-width: 1000px) {
  .amenity-grid { grid-template-columns: repeat(3, 1fr); }
}
</style>
